import { useMemo, useState } from 'react';
import {
  filterNotifications,
  useNotifications,
} from '@/modules/notifications/hooks/useNotifications';
import { NotificationItem } from '@/modules/notifications/components/NotificationItem';
import type {
  NotificationFilters,
  NotificationType,
} from '@/types/notification';
import { useAuth } from '@/shared/hooks/useAuth';

const TYPE_OPTIONS: { value: '' | NotificationType; label: string }[] = [
  { value: '', label: 'Todos los tipos' },
  { value: 'reservation_created', label: 'Reserva creada' },
  { value: 'reservation_conflict', label: 'Conflicto reserva' },
  { value: 'reservation_cancelled', label: 'Reserva cancelada' },
  { value: 'commercial_operation_created', label: 'Operación comercial' },
  { value: 'invoice_created', label: 'Factura creada' },
  { value: 'invoice_pending', label: 'Factura pendiente' },
  { value: 'logistics_scheduled', label: 'Logística' },
  { value: 'maintenance_scheduled', label: 'Mantenimiento programado' },
  { value: 'maintenance_started', label: 'Mantenimiento iniciado' },
  { value: 'maintenance_completed', label: 'Mantenimiento completado' },
  { value: 'maintenance_conflict', label: 'Conflicto mantenimiento' },
  { value: 'inventory_transfer', label: 'Transferencia' },
  { value: 'inventory_adjustment', label: 'Ajuste inventario' },
  { value: 'inventory_discrepancy', label: 'Discrepancia inventario' },
  { value: 'inventory_movement_critical', label: 'Movimiento crítico' },
  { value: 'permission_denied', label: 'Permiso denegado' },
  { value: 'system_alert', label: 'Sistema' },
];

export function NotificationsPage() {
  const { firebaseUser } = useAuth();
  const { notifications, unreadCount, loading, error, markRead, markAllRead } =
    useNotifications(firebaseUser?.uid);

  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>(
    'all',
  );
  const [typeFilter, setTypeFilter] = useState<'' | NotificationType>('');
  const [search, setSearch] = useState('');

  const filters: NotificationFilters = useMemo(() => {
    const f: NotificationFilters = {};
    if (readFilter === 'unread') f.read = false;
    if (readFilter === 'read') f.read = true;
    if (typeFilter) f.type = typeFilter;
    if (search.trim()) f.search = search.trim();
    return f;
  }, [readFilter, typeFilter, search]);

  const filtered = useMemo(
    () => filterNotifications(notifications, filters),
    [notifications, filters],
  );

  return (
    <section className="notifications-page">
      <header className="notifications-page__header">
        <h1>Notificaciones</h1>
        {unreadCount > 0 ? (
          <span className="notification-unread-pill">
            {unreadCount} sin leer
          </span>
        ) : null}
      </header>

      <div className="notifications-page__toolbar inventory-panel">
        <label className="auth-label">
          Estado
          <select
            value={readFilter}
            onChange={(e) =>
              setReadFilter(e.target.value as 'all' | 'unread' | 'read')
            }
          >
            <option value="all">Todas</option>
            <option value="unread">Sin leer</option>
            <option value="read">Leídas</option>
          </select>
        </label>
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as '' | NotificationType)
            }
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label notifications-page__search">
          Buscar
          <input
            type="search"
            placeholder="Título o mensaje…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {unreadCount > 0 ? (
          <button
            type="button"
            className="auth-button auth-button--secondary"
            onClick={() => void markAllRead()}
          >
            Marcar todas como leídas
          </button>
        ) : null}
      </div>

      {loading ? <p className="auth-muted">Cargando notificaciones…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      {!loading && !error && filtered.length === 0 ? (
        <p className="auth-muted notifications-page__empty">
          No hay notificaciones con estos filtros.
        </p>
      ) : null}

      <ul className="notifications-page__list">
        {filtered.map((n) => (
          <li key={n.id}>
            <NotificationItem
              notification={n}
              onMarkRead={(id) => void markRead(id)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
